import React, { useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { callEventPopup, openPopup2 } from "../../../../contexts/PopupContext";
import { VesselSideService } from "../../../../util/services/vessel-side";
import { ACTION_TYPE, OPERATION_CONFIRM_STATUS, OPERATION_TYPE } from "../../../../util/varibles/constants";
import { getElById, showErrorResponse } from "../../../../util/varibles/global";
import { selectOverview } from "../../../../util/store/selectors";
import { IActivityLog, IOperation, IOperation2, IVessel, TOperation } from "../../../../util/varibles/interface";
import { notify } from "../../../../util/varibles/message";
import { template, TemplateCode } from "../../../../util/varibles/template";
import { loginActions } from "../../../LoginPage/reducer";
import { Header } from "../../../PlanOperationPage/Popup/EditOp";
import { EditOperationProvider, getEditOp, setEditOp, useEditOp } from "../../../PlanOperationPage/Popup/EditOp/EditOperationContext";
import { IUpdateOp } from "../../../PlanOperationPage/constants";
import { generate } from "../../../PlanOperationPage/util";
import { focusOp, updateCrewTasksTime, updateTime } from "../../../PlanOperationPage/util/function_operation/global";
import { overviewActions } from "../../reducer";

type LazyViewKey = OPERATION_TYPE.EVENT | OPERATION_TYPE.SERVICE;

const loadViews: Record<LazyViewKey, () => Promise<{ default: React.ComponentType<any> }>> = {
    [OPERATION_TYPE.EVENT]: () => import("../../../PlanOperationPage/Popup/EditOp/Event"),
    [OPERATION_TYPE.SERVICE]: () => import("./Service"),
};


const views: Partial<Record<LazyViewKey, React.ComponentType<any>>> = {};

const isLazyViewKey = (opType?: OPERATION_TYPE): opType is LazyViewKey => {
    return opType === OPERATION_TYPE.EVENT || opType === OPERATION_TYPE.SERVICE;
};

const Body = () => {
    const dispatch = useDispatch();
    const set = setEditOp();
    const get = getEditOp();
    const { onClose } = callEventPopup();
    const [opType] = useEditOp(state => state.opType);
    const Node = isLazyViewKey(opType) ? views[opType] : undefined;

    useEffect(() => {
        const handleSave = (params: IUpdateOp) => {
            const { add = {}, update = {} } = params;
            const id = Object.keys({ ...add, ...update })[0];
            const action_type = !!add[id] ? OPERATION_CONFIRM_STATUS.NEW : OPERATION_CONFIRM_STATUS.UPDATE;
            const op = { ...{ ...add, ...update }[id], action_type };

            const vessels = [
                {
                    vessel_id: op.vessel.id,
                    vessel_owner_id: op.vessel.tenant_id,
                    operations: [generate(op as IOperation2, { status: op.operation.status })]
                }
            ]
            const el = getElById(id + '-loading');
            if (el) {
                el.dataset.loading = "true";
            }
            set({ loadingSubmit: true });
            VesselSideService.crewSend({ vessels })
                .then((rs: any) => {
                    const { new_operations, old_operations } = rs;
                    const { vessel } = op;
                    const originalStart = get().startTime;
                    const normalizeServiceOperation = (item: IOperation) => {
                        if (item.operation.operation_type !== OPERATION_TYPE.SERVICE || originalStart === undefined)
                            return item;

                        const activityStart = item.activity_log?.[0]?.est_start_time;
                        const activityFinish = item.activity_log?.[item.activity_log.length - 1]?.est_start_time;
                        if (activityStart === undefined || activityFinish === undefined)
                            return item;

                        const timeOffset = originalStart - activityStart;

                        return {
                            ...item,
                            activity_log: updateTime(item.activity_log, 0, originalStart),
                            operation: {
                                ...item.operation,
                                duration: activityFinish - activityStart,
                                crew_tasks: updateCrewTasksTime(item.operation.crew_tasks, timeOffset),
                            },
                        };
                    };

                    const { newCode, newOps } = new_operations.reduce((rs: any, item: any) => {
                        item = normalizeServiceOperation(item);
                        rs.newCode = [...rs.newCode, `#${item.operation.operation_code}`];
                        rs.newOps = [...rs.newOps, { ...item, vessel }];
                        return rs;
                    }, { newCode: [], newOps: [] });

                    const { oldCode, oldOps } = old_operations.reduce((rs: { oldCode: string[], oldOps: IOperation[] }, item: IOperation) => {
                        item = normalizeServiceOperation(item);
                        rs.oldCode = [...rs.oldCode || [], `#${item.operation.operation_code}`];
                        rs.oldOps = [...rs.oldOps || [], { ...item, vessel }];
                        return rs;
                    }, { oldCode: [], oldOps: [] });

                    notify.success({
                        message: 'Update successfully',
                        description: template[TemplateCode.T2]('', newCode, oldCode)
                    });
                    dispatch(overviewActions.addOps(newOps))
                    dispatch(overviewActions.updateOps(oldOps))
                    dispatch(loginActions.updateOpsOfVessel([...newOps, ...oldOps]));
                    if (newOps.length > 0) {
                        focusOp(newOps[0].operation.id);
                    }
                    set({ loadingSubmit: false });
                    if (el) {
                        delete el.dataset.loading;
                    }
                    setTimeout(onClose, 300);
                })
                .catch(error => {
                    showErrorResponse('Update failed', error)
                    set({ loadingSubmit: false });
                    if (el) {
                        delete el.dataset.loading;
                    }
                })
        };
        set({ onSave: handleSave })
    }, [dispatch, get, onClose, set])

    return <div className="h-full max-h-[calc(100vh-60px)] overflow-auto" >
        {!!Node && <Node />}
    </div>
}

interface IProps {
    editMode: ACTION_TYPE
    data: {
        vessel: IVessel
        operation: TOperation
        activity_log: IActivityLog[]
    }
}

const PopupEdit: React.FC<IProps> = (props) => {
    const { data, editMode } = props;
    const operations = useSelector(selectOverview.ops);
    const level = useSelector(selectOverview.level);
    const { onClose } = callEventPopup();
    const opType = data.operation.operation_type;

    return <EditOperationProvider
        opType={opType}
        operation={data.operation}
        vessel={data.vessel}
        activity_log={data.activity_log}
        editMode={editMode}
        operations={operations}
        level={level}
        onClose={onClose}
        startTime={data.activity_log[0].est_start_time}
    >
        <Header onClose={onClose} />
        <Body />
    </EditOperationProvider>
};

export const openEdit = async (props: IProps) => {
    const opType = props.data.operation.operation_type;

    if (!isLazyViewKey(opType)) return;

    if (!views[opType]) {
        const mod = await loadViews[opType]();
        views[opType] = mod.default;
    }

    openPopup2(<PopupEdit {...props} />, {
        modalProps: {
            width: opType === OPERATION_TYPE.EVENT ? 500 : 1200,
            styles: { container: { padding: 0, height: '100%' } }
        }
    })
}

export default PopupEdit
